import { useTranslation } from 'react-i18next';

export interface BarListDatum {
  readonly id: string;
  readonly label: string;
  readonly value: number;
}

interface BarListProps {
  readonly data: readonly BarListDatum[];
  /** Accessible name for the list as a whole. */
  readonly ariaLabel: string;
  readonly valueFormatter?: (value: number) => string;
}

/**
 * A ranked horizontal bar list (e.g. coverage-request demand by city). Built
 * from real text + CSS flex/grid rather than SVG, so it's accessible with no
 * extra table fallback needed. The fill track grows from the inline-start edge
 * (right in RTL, left in LTR) automatically — no left/right hardcoding.
 */
export function BarList({ data, ariaLabel, valueFormatter }: BarListProps): JSX.Element {
  const { t } = useTranslation();

  if (data.length === 0) {
    return <p className="chart__empty">{t('common.empty')}</p>;
  }

  const format = valueFormatter ?? ((v: number) => String(v));
  const max = Math.max(1, ...data.map((d) => d.value));

  return (
    <ul className="bar-list" aria-label={ariaLabel}>
      {data.map((d) => (
        <li key={d.id} className="bar-list__row">
          <span className="bar-list__label">{d.label}</span>
          <span className="bar-list__track">
            <span className="bar-list__fill" style={{ width: `${(d.value / max) * 100}%` }} />
          </span>
          <span className="bar-list__value">{format(d.value)}</span>
        </li>
      ))}
    </ul>
  );
}
